import { trackCtaClick } from '#utils/utils';
import {
  Badge,
  Box,
  Button,
  Container,
  Divider,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronDown,
  FaCogs,
  FaGraduationCap,
  FaHeadset,
  FaMapMarkedAlt,
} from 'react-icons/fa/index.js';

const MotionBox = motion(Box);

const STEPS = [
  {
    number: '01',
    icon: FaCalendarAlt,
    title: 'Discovery Session',
    duration: '1–2 hours',
    color: 'blue',
    gradient: 'linear(to-br, blue.500, cyan.400)',
    description:
      'We meet (video or in-person) to understand your business, your team, and the specific process causing the most friction. You leave with a clear assessment of what can be automated and what the outcome will be.',
    youDo: ['Describe your current workflow', 'Share any existing documentation', 'Tell us what good looks like'],
    weDo: ['Ask our standard discovery questions', 'Identify automation candidates', 'Assess your existing tools and data'],
  },
  {
    number: '02',
    icon: FaMapMarkedAlt,
    title: 'Workflow Map',
    duration: '3–5 business days',
    color: 'purple',
    gradient: 'linear(to-br, purple.500, pink.400)',
    description:
      'Before we write a single line of code, you receive a visual map of exactly what will be automated. Every trigger, every action, every integration — documented and approved by you before we build.',
    youDo: ['Review the workflow diagram', 'Approve or request changes', 'Confirm tool access and credentials'],
    weDo: ['Build the visual workflow map', 'Identify integration requirements', 'Provide a fixed-scope implementation plan'],
  },
  {
    number: '03',
    icon: FaCogs,
    title: 'Implementation',
    duration: '1–3 weeks',
    color: 'orange',
    gradient: 'linear(to-br, orange.500, yellow.400)',
    description:
      'We build and configure every automation on the TenK Platform. Integrations are connected, workflows are tested with real data, and edge cases are handled before you see a single result.',
    youDo: ['Provide API keys or tool access', 'Review test results with sample data', 'Approve before go-live'],
    weDo: ['Build all automations on our platform', 'Connect every integration', 'Test exhaustively with your real data'],
  },
  {
    number: '04',
    icon: FaGraduationCap,
    title: 'Team Training',
    duration: '2–4 hours',
    color: 'green',
    gradient: 'linear(to-br, green.500, teal.400)',
    description:
      'Your team learns how the system works, what to do when exceptions occur, and how to add new knowledge or trigger workflows manually. No technical background required.',
    youDo: ['Attend the training session', 'Ask every question you have', 'Designate an internal point of contact'],
    weDo: ['Run a structured training session', 'Provide written documentation', 'Record a walkthrough video for future staff'],
  },
  {
    number: '05',
    icon: FaHeadset,
    title: '30-Day Support',
    duration: '30 days post-launch',
    color: 'teal',
    gradient: 'linear(to-br, teal.500, cyan.400)',
    description:
      'We monitor the system during your first full month of live operation. Edge cases get handled, adjustments get made, and you have direct access to us if anything needs attention.',
    youDo: ['Use the system normally', 'Flag anything that feels off', 'Submit change requests if scope evolves'],
    weDo: ['Monitor system performance daily', 'Fix any issues within 24 hours', 'Provide a 30-day health report'],
  },
];

const FAQS = [
  { q: 'How long does the full process take?', a: 'Most Launch engagements complete in 3–5 weeks. Optimize engagements typically run 6–10 weeks. Timeline depends on scope, tool access, and your team\'s availability during discovery.' },
  { q: 'Do I need IT staff or technical knowledge?', a: 'No. You describe the business problem. We handle all technical implementation. You only need to approve what we build and attend training.' },
  { q: 'What if my process changes after we build?', a: 'That\'s what Operate is for. Monthly subscribers get up to 2 workflow updates per month included. One-time projects can request changes at our standard consulting rate.' },
  { q: 'Will this break my existing tools?', a: 'No. We connect to your existing tools via their official APIs — we never modify your source systems. Everything is read or written through supported integrations.' },
  { q: 'What happens after the 30-day support period?', a: 'You own the system. It keeps running without us. If you want ongoing improvements and monitoring, you can upgrade to Operate at any time.' },
];

const HowItWorksPage = () => {
  const pageBg = useColorModeValue('gray.50', 'gray.950');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const faqBg = useColorModeValue('gray.50', 'gray.900');
  const tableBorder = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box bg={pageBg}>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, gray.900, blue.900)"
        py={{ base: 20, md: 28 }}
        position="relative"
        overflow="hidden"
      >
        <Box position="absolute" top="-60px" right="-60px" w="350px" h="350px" borderRadius="full" bg="whiteAlpha.50" pointerEvents="none" />
        <Container maxW="container.lg" textAlign="center">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 } as any}
          >
            <Badge bg="blue.500" color="white" fontSize="sm" px={4} py={1} borderRadius="full" mb={6} display="inline-block">
              The Process
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl' }}
              fontWeight="extrabold"
              color="white"
              lineHeight="1.1"
              letterSpacing="-0.03em"
              mb={6}
            >
              How we eliminate
              <br />
              your business process problem.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.700" maxW="2xl" mx="auto" mb={10}>
              Five steps. Fixed scope. No surprises.
              Here is exactly what happens from the moment you contact us
              to the moment your team stops touching the process.
            </Text>
            <HStack justify="center" spacing={4} flexWrap="wrap">
              <Button
                size="lg" bg="white" color="blue.800" fontWeight="bold" px={10}
                as="a" href="/contact-us?interest=discovery"
                onClick={() => trackCtaClick({ cta_label: 'Start With Discovery', cta_location: 'HowItWorks Hero', destination_url: '/contact-us?interest=discovery' })}
                _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
                transition="all 0.2s"
              >
                Start With Discovery
              </Button>
              <Button
                size="lg" variant="outline" borderColor="whiteAlpha.500" color="white" px={8}
                as="a" href="/pricing"
                _hover={{ bg: 'whiteAlpha.100' }}
              >
                See Pricing
              </Button>
            </HStack>
          </MotionBox>
        </Container>
      </Box>

      {/* 5 Steps */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={0} align="stretch">
            {STEPS.map((step, i) => (
              <Box key={step.number}>
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 } as any}
                >
                  <Stack
                    direction={{ base: 'column', lg: 'row' }}
                    spacing={8}
                    align="flex-start"
                    py={12}
                  >
                    {/* Step number + icon */}
                    <Box flex="0 0 auto" textAlign={{ base: 'left', lg: 'center' }} minW={{ lg: '120px' }}>
                      <Box
                        w={16} h={16}
                        borderRadius="2xl"
                        bgGradient={step.gradient}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={2}
                        boxShadow="lg"
                      >
                        <Icon as={step.icon} color="white" boxSize={7} />
                      </Box>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color={`${step.color}.500`}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        Step {step.number}
                      </Text>
                    </Box>

                    {/* Content */}
                    <Box flex="1">
                      <HStack spacing={4} mb={3} flexWrap="wrap">
                        <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={headingColor}>
                          {step.title}
                        </Heading>
                        <Badge colorScheme={step.color} fontSize="xs" px={3} py={1} borderRadius="full">
                          {step.duration}
                        </Badge>
                      </HStack>
                      <Text fontSize="md" color={textColor} mb={6} lineHeight="1.7" maxW="2xl">
                        {step.description}
                      </Text>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box
                          bg={faqBg}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius="xl"
                          p={5}
                        >
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={textColor} mb={3}>
                            You do
                          </Text>
                          <VStack spacing={2} align="flex-start">
                            {step.youDo.map((item) => (
                              <HStack key={item} spacing={2}>
                                <Icon as={FaCheckCircle} color="green.400" boxSize={3.5} />
                                <Text fontSize="sm" color={headingColor}>{item}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                        <Box
                          bg={faqBg}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius="xl"
                          p={5}
                        >
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color={textColor} mb={3}>
                            We do
                          </Text>
                          <VStack spacing={2} align="flex-start">
                            {step.weDo.map((item) => (
                              <HStack key={item} spacing={2}>
                                <Icon as={FaCheckCircle} color="blue.400" boxSize={3.5} />
                                <Text fontSize="sm" color={headingColor}>{item}</Text>
                              </HStack>
                            ))}
                          </VStack>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  </Stack>
                </MotionBox>
                {i < STEPS.length - 1 && (
                  <Box textAlign={{ base: 'left', lg: 'center' }} pl={{ base: 0, lg: '52px' }}>
                    <Icon as={FaChevronDown} color={borderColor} boxSize={5} mb={2} />
                  </Box>
                )}
                {i < STEPS.length - 1 && <Divider borderColor={borderColor} />}
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* Week-by-week timeline */}
      <Box bg={useColorModeValue('gray.900', 'gray.950')} py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={10} textAlign="center">
            <Badge colorScheme="blue" fontSize="sm" px={4} py={1} borderRadius="full">Typical Timeline</Badge>
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color="white">
              What a Launch engagement looks like week by week.
            </Heading>
          </VStack>
          <Box overflowX="auto">
            <Table size="sm" borderRadius="xl" overflow="hidden">
              <Thead>
                <Tr bg="blue.800">
                  {['Week', 'Activity', 'Who', 'Deliverable'].map((h) => (
                    <Th key={h} color="white" py={4} borderColor="blue.700" fontSize="xs" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {[
                  { week: 'Week 1', activity: 'Discovery session + workflow mapping', who: 'Both', deliverable: 'Visual workflow diagram' },
                  { week: 'Week 2', activity: 'Platform setup + integration build', who: 'TenK', deliverable: 'Working automation in staging' },
                  { week: 'Week 3', activity: 'Testing + client review + go-live', who: 'Both', deliverable: 'Live system + training session' },
                  { week: 'Weeks 4–7', activity: 'Monitoring + adjustments', who: 'TenK', deliverable: '30-day health report' },
                ].map((row, i) => (
                  <Tr key={row.week} bg={i % 2 === 0 ? 'gray.800' : 'gray.850'}>
                    <Td borderColor={tableBorder} fontWeight="bold" color="blue.300" fontSize="sm">{row.week}</Td>
                    <Td borderColor={tableBorder} color="gray.300" fontSize="sm">{row.activity}</Td>
                    <Td borderColor={tableBorder} color="gray.400" fontSize="sm">{row.who}</Td>
                    <Td borderColor={tableBorder} color="green.300" fontSize="sm">{row.deliverable}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Container>
      </Box>

      {/* FAQ */}
      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.md">
          <VStack spacing={4} mb={12} textAlign="center">
            <Badge colorScheme="blue" fontSize="sm" px={4} py={1} borderRadius="full">FAQ</Badge>
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={headingColor}>
              Common questions about the process.
            </Heading>
          </VStack>
          <VStack spacing={4} align="stretch">
            {FAQS.map((faq, i) => (
              <MotionBox
                key={faq.q}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 } as any}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                p={6}
              >
                <Text fontWeight="bold" color={headingColor} mb={2} fontSize="md">
                  {faq.q}
                </Text>
                <Text fontSize="sm" color={textColor} lineHeight="1.7">
                  {faq.a}
                </Text>
              </MotionBox>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* CTA */}
      <Box bgGradient="linear(to-r, blue.700, blue.500)" py={{ base: 16, md: 20 }}>
        <Container maxW="container.md" textAlign="center">
          <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color="white" mb={4}>
            Ready to start?
          </Heading>
          <Text fontSize="lg" color="whiteAlpha.800" mb={8}>
            Book a 30-minute discovery call. No slides, no pitch. Just a conversation
            about your process and whether we can help.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              size="lg" bg="white" color="blue.700" fontWeight="bold" px={10}
              as="a" href="/contact-us?interest=discovery"
              onClick={() => trackCtaClick({ cta_label: 'Book Discovery Call', cta_location: 'HowItWorks CTA', destination_url: '/contact-us?interest=discovery' })}
              _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
              transition="all 0.2s"
            >
              Book a Discovery Call
            </Button>
            <Button
              size="lg" variant="outline" borderColor="whiteAlpha.500" color="white" px={8}
              as="a" href="/solutions"
              _hover={{ bg: 'whiteAlpha.100' }}
            >
              See Our Solutions
            </Button>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default HowItWorksPage;
